import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import { toDataURL } from 'qrcode';
import { useTranslation } from 'react-i18next';
import { useToast } from '../tavern/ToastProvider';

const safeFilenamePart = (value) =>
  String(value || 'profile')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'profile';

export const writeClipboardText = async (text) => {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

  if (clipboard?.writeText) {
    await clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    throw new Error('clipboard unavailable');
  }

  const textArea = document.createElement('textarea');
  const body = document.body;
  if (!body) throw new Error('clipboard unavailable');

  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  body.appendChild(textArea);
  textArea.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('copy failed');
  } finally {
    if (textArea.parentNode) {
      textArea.parentNode.removeChild(textArea);
    }
  }
};

const ShareProfileDialog = ({ onClose, open, profile, profileUrl }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrError, setQrError] = useState(false);
  const displayName = profile?.displayName || t('profile.defaults.displayName');
  const handle = profile?.username ? `@${profile.username}` : displayName;
  const canShareUrl = Boolean(profileUrl);
  const shareTitle = t('profile.share.nativeTitle', { name: displayName });
  const shareText = t('profile.share.nativeText', { name: displayName });
  const downloadName = useMemo(
    () => `thetavern-${safeFilenamePart(profile?.username || displayName)}-qr.png`,
    [displayName, profile?.username]
  );

  useEffect(() => {
    let mounted = true;

    if (!open || !profileUrl) {
      setQrDataUrl('');
      setQrError(false);
      return undefined;
    }

    setQrError(false);
    toDataURL(
      profileUrl,
      {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 360,
        color: {
          dark: '#062522',
          light: '#fff8e8',
        },
      },
      (error, dataUrl) => {
        if (error) {
          if (mounted) setQrError(true);
          return;
        }

        if (mounted) setQrDataUrl(dataUrl);
      }
    );

    return () => {
      mounted = false;
    };
  }, [open, profileUrl]);

  const copyProfileUrl = async () => {
    if (!profileUrl) return;

    try {
      await writeClipboardText(profileUrl);
      toast.success(t('profile.share.feedback.copied'));
    } catch {
      toast.error(t('profile.share.feedback.copyError'));
    }
  };

  const handleShare = async () => {
    if (!profileUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: profileUrl,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    await copyProfileUrl();
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = downloadName;
    link.click();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      aria-labelledby="share-profile-title"
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: '100%' },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: 'calc(100% - 64px)' },
        },
      }}
      PaperProps={{
        sx: {
          overflow: 'hidden',
          bgcolor: 'primary.dark',
          background:
            'radial-gradient(circle at 24% 10%, rgba(241,216,155,0.35), transparent 13rem), linear-gradient(145deg, #0F4C45, #062522)',
          color: '#fff8e8',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', p: { xs: 1.75, sm: 3 } }}>
          <IconButton
            aria-label={t('common.close')}
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#fff8e8',
              bgcolor: 'rgba(255,248,232,0.12)',
              '&:hover': { bgcolor: 'rgba(255,248,232,0.2)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Stack spacing={{ xs: 1.25, sm: 2.5 }} alignItems="center" sx={{ pt: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ textAlign: 'center', px: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'secondary.light', mb: { xs: 0.25, sm: 0.75 } }}
              >
                {t('profile.share.eyebrow')}
              </Typography>
              <Typography
                id="share-profile-title"
                variant="h2"
                sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                {t('profile.share.title')}
              </Typography>
            </Box>

            <Box
              sx={{
                width: '100%',
                borderRadius: 2,
                p: { xs: 1.25, sm: 2 },
                bgcolor: '#fff8e8',
                color: 'text.primary',
                boxShadow: '0 22px 46px -28px rgba(0,0,0,0.65)',
              }}
            >
              <Stack spacing={{ xs: 0.75, sm: 1.5 }} alignItems="center">
                <Avatar
                  src={profile?.avatarUrl || undefined}
                  alt={displayName}
                  sx={{
                    width: { xs: 46, sm: 64 },
                    height: { xs: 46, sm: 64 },
                    border: '3px solid',
                    borderColor: 'secondary.main',
                    boxShadow: 2,
                  }}
                />
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: { xs: 'min(58dvh, 236px)', sm: 280 },
                    aspectRatio: '1 / 1',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff8e8',
                  }}
                >
                  {qrDataUrl ? (
                    <Box
                      component="img"
                      src={qrDataUrl}
                      alt={t('profile.share.qrAlt', { name: displayName })}
                      sx={{ width: '100%', height: '100%', display: 'block' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                      {qrError ? t('profile.share.qrError') : t('profile.share.qrLoading')}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 0, width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: '"Cinzel", Georgia, serif',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', sm: '1.15rem' },
                    }}
                    noWrap
                  >
                    {displayName}
                  </Typography>
                  <Typography color="text.secondary" noWrap sx={{ fontSize: { xs: '0.88rem', sm: '1rem' } }}>
                    {handle}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {!canShareUrl ? (
              <Alert severity="warning" sx={{ width: '100%' }}>
                {t('profile.share.missingUrl')}
              </Alert>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  width: '100%',
                  color: 'rgba(255,248,232,0.78)',
                  textAlign: 'center',
                  overflowWrap: 'anywhere',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {profileUrl}
              </Typography>
            )}

            <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} sx={{ width: '100%' }}>
              <Button
                fullWidth
                aria-label={t('profile.share.actions.share')}
                variant="contained"
                color="secondary"
                startIcon={<IosShareIcon />}
                onClick={handleShare}
                disabled={!canShareUrl}
                sx={{
                  minWidth: 0,
                  px: { xs: 0.5, sm: 2 },
                  py: { xs: 0.75, sm: 0.875 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {t('profile.share.actions.share')}
                </Box>
              </Button>
              <Button
                fullWidth
                aria-label={t('profile.share.actions.copy')}
                variant="outlined"
                color="secondary"
                startIcon={<ContentCopyIcon />}
                onClick={copyProfileUrl}
                disabled={!canShareUrl}
                sx={{
                  color: '#fff8e8',
                  borderColor: 'rgba(241,216,155,0.62)',
                  minWidth: 0,
                  px: { xs: 0.5, sm: 2 },
                  py: { xs: 0.75, sm: 0.875 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {t('profile.share.actions.copy')}
                </Box>
              </Button>
              <Button
                fullWidth
                aria-label={t('profile.share.actions.download')}
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!qrDataUrl}
                sx={{
                  color: '#fff8e8',
                  borderColor: 'rgba(241,216,155,0.62)',
                  minWidth: 0,
                  px: { xs: 0.5, sm: 2 },
                  py: { xs: 0.75, sm: 0.875 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {t('profile.share.actions.download')}
                </Box>
              </Button>
            </Stack>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,248,232,0.7)',
                textAlign: 'center',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {t('profile.share.publicProfileNote')}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShareProfileDialog;
